import React from "react";
import { CircleMarker, Polyline, Popup } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { PathOptions } from "leaflet";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "../redux/hooks";
import { dynamicTextColor, routeColorForTableComponent, shapeArrayOperations } from "../utils/helpers";
import { getWheelColors } from "../utils/helperWheelColors";
import { stopsOptionsConstant } from "../data/constants";
import { StopAvailableRoutes } from "./helpers/Helpers";
import { PopupContainer } from "./views/SelectedBusViewMap";

//^ Reusable component!
export const UseTablesMapsStopsShape = (): JSX.Element => {
  const [activeBusData, agencyName, routesInfo]: [ActiveBusData, string, RouteDetails[]] = useAppSelector(
    (state: RootState) => [state?.buses?.activeBusData, state?.routesInfo?.agencyName, state?.routesInfo?.routesInfo],
    shallowEqual
  );

  const [stops, setStops] = React.useState<Array<BusStop>>([]);
  const [shape, setShape] = React.useState<Array<Line>>([]);
  // console.log({ stops, shape });

  React.useEffect(() => {
    if (activeBusData) {
      const selectedDirection = activeBusData?.tableRouteData?.selectedDirection;
      // console.log({ selectedDirection });
      if (selectedDirection) {
        const lineStops = activeBusData?.tableRouteData?.lineStops.filter(
          (stopSet: BusStopDirection) => stopSet.id === selectedDirection
        );
        const lineShape: Location[] = activeBusData?.tableRouteData?.lineShape.filter(
          (shape: { directionId: string }) => shape.directionId === selectedDirection
        );
        // console.log({ lineShape });
        const flatArrayLineShape: Line[] = shapeArrayOperations(lineShape);
        // console.log({ selectedDirection, lineStops, flatArrayLineShape });
        setStops(lineStops[0]?.stop);
        setShape(flatArrayLineShape);
      }

      if (selectedDirection === "---" && agencyName === "City of Sweetwater") {
        // console.log({ agencyName });
        // console.log("activeBusData:", activeBusData);
        const lineStops: BusStopDirection[] = activeBusData?.tableRouteData?.lineStops;
        const lineShape: Location[] = activeBusData?.tableRouteData?.lineShape;
        const flatArrayLineShape: Line[] = shapeArrayOperations(lineShape);

        setStops(lineStops[0]?.stop);
        setShape(flatArrayLineShape);
      }

      // return () => {
      //   setStops([]);
      //   setShape([]);
      // };
    }
  }, [activeBusData, agencyName]);

  return (
    <React.Fragment>
      {shape ? <PathShape shape={shape} routesInfo={routesInfo} activeBusData={activeBusData} /> : null}
      {stops ? <LineStops stops={stops} routesInfo={routesInfo} activeBusData={activeBusData} /> : null}
    </React.Fragment>
  );
};

//* The main component
const TableBigComponentMap = (): JSX.Element => {
  return <UseTablesMapsStopsShape />;
};

export default TableBigComponentMap;

const PathShape = ({
  shape,
  routesInfo,
  activeBusData,
}: {
  shape: Line[];
  routesInfo: RouteDetails[];
  activeBusData: ActiveBusData;
}): JSX.Element => {
  const directionOptions = {
    color: routeColorForTableComponent(routesInfo, activeBusData),
    weight: 5,
    opacity: 1,
  } as PathOptions;

  const directionOptionsLight = {
    color: routeColorForTableComponent(routesInfo, activeBusData),
    weight: 3,
    opacity: 0.75,
  } as PathOptions;

  return (
    <React.Fragment>
      {shape && shape?.length >= 1
        ? (shape.map((elem: Line, index: number): JSX.Element => {
            return (
              <Polyline
                key={index}
                pathOptions={index === shape?.length - 1 ? directionOptions : directionOptionsLight}
                positions={elem}
                children={
                  <Popup>
                    <ContainerPopup
                      routeInfo={
                        routesInfo?.filter(
                          (route: RouteDetails) => route?.id === activeBusData?.tableRouteData?.routeId
                        )?.[0]
                      }
                    />
                  </Popup>
                }
              />
            );
          }) as JSX.Element[])
        : null}
    </React.Fragment>
  );
};

const LineStops = ({
  stops,
  routesInfo,
  activeBusData,
}: {
  stops: BusStop[];
  routesInfo: RouteDetails[];
  activeBusData: ActiveBusData;
}): JSX.Element => {
  const { t } = useTranslation();

  const wheelColors: string[] = getWheelColors(routeColorForTableComponent(routesInfo, activeBusData), 2);
  const stopsOptions = {
    ...stopsOptionsConstant,
    fillColor: wheelColors?.[0],
    color: wheelColors?.[1],
  };

  return (
    <React.Fragment>
      {stops && stops?.length >= 1
        ? (stops.map(
            (elem: BusStop, index: number): JSX.Element => (
              <CircleMarker
                pathOptions={stopsOptions}
                key={String(elem.lat + " " + elem.lon + " " + index)}
                center={{
                  lat: elem.lat,
                  lng: elem.lon,
                }}
                radius={6}
              >
                <Popup>
                  <div>
                    {t("Nazwa")} <span className="span_bold">{elem.name}</span>
                    <br />
                    {t("Id")} <span className="span_bold">{elem.id}</span>
                    <br />
                    <small className="text-muted">
                      {t("Pozycja")}: <span className="span_bold text-muted">{`${elem?.lat} ${elem?.lon}`}</span>
                    </small>
                  </div>
                  <StopAvailableRoutes stopId={elem?.id} />
                </Popup>
              </CircleMarker>
            )
          ) as JSX.Element[])
        : null}
    </React.Fragment>
  );
};

//* Route Popup
const ContainerPopup = ({ routeInfo }: { routeInfo: RouteDetails }): JSX.Element => {
  const { t } = useTranslation();

  return (
    <PopupContainer>
      <div className="popup-line">
        {t("Id")}{" "}
        <span
          style={{
            background: routeInfo?.color,
            color: dynamicTextColor(routeInfo?.color) || "#000000",
            padding: "0.25rem 0.5rem",
          }}
        >
          {routeInfo?.shortName || routeInfo?.id || t("b/d")}
        </span>
      </div>
      <div className="popup-line">
        {t("Nazwa")} <span className="span_maroon">{routeInfo?.longName || t("b/d")}</span>
      </div>
    </PopupContainer>
  );
};
